/**
 * Elite 4 Trials Data
 *
 * This file now fetches trial data from the backend API.
 * The backend is the single source of truth for Elite 4 stats and data.
 */

import { apiGetTrialRegions } from '../services/apiService';

// Cache for trial data
let cachedTrialData = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch Elite 4 trial data from the backend
 * Results are cached for 5 minutes
 */
export const fetchTrialData = async () => {
  const now = Date.now();

  // Return cached data if still valid
  if (cachedTrialData && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedTrialData;
  }

  try {
    const data = await apiGetTrialRegions();
    if (data) {
      cachedTrialData = data;
      cacheTimestamp = now;
      return data;
    }
  } catch (error) {
    console.error('Failed to fetch trial data:', error);
  }

  // Return cached data as fallback if fetch fails
  return cachedTrialData;
};

/**
 * Get trial regions (for use in components that have already loaded data)
 */
export const getTrialRegions = () => {
  return cachedTrialData?.regions || {};
};

/**
 * Get trial region keys
 */
export const getTrialRegionKeys = () => {
  return cachedTrialData?.regionKeys || [];
};

/**
 * Get trial rewards
 */
export const getTrialRewards = () => {
  return cachedTrialData?.rewards || { primos: 1000 };
};

/**
 * Get a specific Elite 4 member
 */
export const getElite4Member = (regionKey, memberIndex) => {
  const regions = getTrialRegions();
  const region = regions[regionKey];
  return region?.members[memberIndex] || null;
};

// Export for backwards compatibility (will be empty until data is fetched)
export const ELITE_4_TRIALS = new Proxy({}, {
  get: (target, prop) => {
    const regions = getTrialRegions();
    return regions[prop];
  },
  ownKeys: () => {
    return getTrialRegionKeys();
  },
  getOwnPropertyDescriptor: (target, prop) => {
    const regions = getTrialRegions();
    if (regions[prop]) {
      return { enumerable: true, configurable: true, value: regions[prop] };
    }
    return undefined;
  }
});

export const TRIAL_REGIONS = new Proxy([], {
  get: (target, prop) => {
    const keys = getTrialRegionKeys();
    if (prop === 'length') return keys.length;
    if (prop === Symbol.iterator) return keys[Symbol.iterator].bind(keys);
    if (typeof prop === 'string' && !isNaN(prop)) return keys[parseInt(prop)];
    if (prop === 'map') return keys.map.bind(keys);
    if (prop === 'forEach') return keys.forEach.bind(keys);
    if (prop === 'filter') return keys.filter.bind(keys);
    if (prop === 'some') return keys.some.bind(keys);
    if (prop === 'includes') return keys.includes.bind(keys);
    return keys[prop];
  }
});

export const TRIAL_REWARDS = new Proxy({ primos: 1000 }, {
  get: (target, prop) => {
    const rewards = getTrialRewards();
    return rewards[prop] ?? target[prop];
  }
});
